import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bot, Loader2, AlertCircle, RefreshCw, Send } from "lucide-react";
import {
  refreshArtifact,
  getArtifactStatus,
  askArtifactStream,
  type ArtifactStatus,
  type ChatAgentResponse,
  type AnswerBlock,
  type Citation,
} from "../../lib/commentArtifactService";

interface CommentArtifactChatProps {
  docketNumber: string;
}

interface UiMessage {
  id: string;
  role: "user" | "assistant";
  text?: string; // for user messages
  response?: ChatAgentResponse; // for assistant messages
  streamingContent?: string; // mid-stream prose for in-progress assistant messages
  isStreaming?: boolean;
  error?: string;
}

// Cap how many prior turns we send to the classifier. Matches the
// server-side window (`history[-6:]` in chat.artifact.intents).
const HISTORY_WINDOW = 6;

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_DURATION_MS = 5 * 60 * 1000; // give up polling after 5 min

/**
 * Comment Analysis Chat — for attorneys drafting client comments.
 *
 * Behavior:
 *  - On mount, fires POST /refresh. If 'fresh', enables chat immediately.
 *    If 'queued'/'running', polls /status until 'fresh'.
 *  - Chat input enabled as soon as the artifact has any rows
 *    (`status.has_artifact`), so users aren't blocked by ongoing
 *    incremental compiles.
 *  - "Compiling N new comments…" badge while a delta runs.
 *  - "Refresh" button for the deferred-cost-guard case (>50 new
 *    comments since last compile).
 */
export const CommentArtifactChat: React.FC<CommentArtifactChatProps> = ({
  docketNumber,
}) => {
  const [status, setStatus] = useState<ArtifactStatus | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  const [isAsking, setIsAsking] = useState(false);
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ----- refresh-on-mount + status polling --------------------------

  const fetchStatus = useCallback(async () => {
    try {
      const s = await getArtifactStatus(docketNumber);
      setStatus(s);
      return s;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setRefreshError(msg);
      return null;
    }
  }, [docketNumber]);

  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    async function poll() {
      const s = await fetchStatus();
      if (!s) return;
      if (s.active_compile_job) {
        if (Date.now() - startedAt < POLL_MAX_DURATION_MS && !cancelled) {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    }

    async function start() {
      try {
        const refresh = await refreshArtifact(docketNumber);
        if (cancelled) return;
        if (refresh.status === "queued" || refresh.status === "running") {
          // Immediately fetch and start polling
          poll();
        } else {
          // 'fresh' or 'deferred' — fetch status once for UI
          await fetchStatus();
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (!cancelled) setRefreshError(msg);
      }
    }

    start();
    return () => {
      cancelled = true;
    };
  }, [docketNumber, fetchStatus]);

  // ----- ask flow ---------------------------------------------------

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || isAsking) return;
    setInput("");
    const userMsg: UiMessage = {
      id: `u_${Date.now()}`,
      role: "user",
      text,
    };

    // Build the history we send to the classifier. Server caps at 6
    // turns but we filter to the same window here to keep the
    // payload tight.
    const history = messages
      .slice(-HISTORY_WINDOW)
      .map((m) => {
        if (m.role === "user") {
          return { role: "user", content: m.text ?? "" };
        }
        // Assistant message → flatten response blocks to a plain
        // string so the classifier has context. Truncate per turn to
        // keep token cost flat.
        const flat = (m.response?.answer_blocks ?? [])
          .map((b) => b.content)
          .join("\n");
        return { role: "assistant", content: flat.slice(0, 1000) };
      })
      .filter((t) => t.content.trim().length > 0);

    const assistantId = `a_${Date.now()}`;
    const pendingMsg: UiMessage = {
      id: assistantId,
      role: "assistant",
      streamingContent: "",
      isStreaming: true,
    };
    setMessages((prev) => [...prev, userMsg, pendingMsg]);
    setIsAsking(true);

    try {
      await askArtifactStream(
        docketNumber,
        { question: text, history },
        {
          onChunk: (delta) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, streamingContent: (m.streamingContent ?? "") + delta }
                  : m,
              ),
            );
          },
          onDone: (response) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, response, streamingContent: undefined, isStreaming: false }
                  : m,
              ),
            );
          },
          onError: (detail) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, error: detail, isStreaming: false, streamingContent: undefined }
                  : m,
              ),
            );
          },
        },
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? { ...m, error: msg, isStreaming: false, streamingContent: undefined }
            : m,
        ),
      );
    } finally {
      setIsAsking(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [input, isAsking, docketNumber, messages]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        send();
      }
    },
    [send],
  );

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ----- derived flags ----------------------------------------------

  const hasArtifact = !!status?.has_artifact;
  const isCompiling = !!status?.active_compile_job;
  const driftN = status?.drift_n ?? 0;
  const lastFailed = status?.last_failed_job;

  const inputDisabled = isAsking || (!hasArtifact && !isCompiling);

  const compilingBadge = useMemo(() => {
    if (!isCompiling) return null;
    const job = status!.active_compile_job!;
    const newN = job.new_comments_processed ?? driftN;
    return (
      <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200/50">
        <Loader2 className="w-3 h-3 animate-spin" />
        <span>Indexing {newN > 0 ? newN : "new"} comments…</span>
      </div>
    );
  }, [isCompiling, status, driftN]);

  // ----- render -----------------------------------------------------

  return (
    <div className="flex flex-col h-[600px] bg-white rounded-lg overflow-hidden">
      {/* Status bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="text-xs text-gray-500">
            {status ? (
              <>
                {status.comments_in_artifact} comment
                {status.comments_in_artifact !== 1 ? "s" : ""} indexed
                {driftN > 0 && status.has_artifact
                  ? ` · ${driftN} pending`
                  : ""}
              </>
            ) : refreshError ? (
              <span className="text-red-600">Status unavailable</span>
            ) : (
              "Loading…"
            )}
          </div>
          {compilingBadge}
        </div>
        {driftN > 200 && !isCompiling && (
          <button
            onClick={() => refreshArtifact(docketNumber).then(() => fetchStatus())}
            className="flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-900 px-2 py-1"
            title={`${driftN} new comments since last compile`}
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        )}
      </div>

      {/* Last-failed banner */}
      {lastFailed && lastFailed.error && !isCompiling && (
        <div className="flex items-start gap-2 px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-800">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Last compile failed</div>
            <div className="text-red-700/90">{lastFailed.error}</div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 && (
          <EmptyState hasArtifact={hasArtifact} isCompiling={isCompiling} />
        )}
        {messages.map((m) => (
          <MessageRow key={m.id} message={m} />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 p-3 bg-gray-50">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={inputDisabled}
            placeholder={
              hasArtifact
                ? 'Ask about positions, filers, arguments, authorities, gaps… (Shift+Enter for new line)'
                : isCompiling
                  ? "Indexing comments — chat will be available shortly…"
                  : "No comments indexed yet"
            }
            rows={2}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
          />
          <button
            onClick={send}
            disabled={inputDisabled || !input.trim()}
            className="flex items-center justify-center w-10 h-10 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-md transition"
            aria-label="Send"
          >
            {isAsking ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-gray-500">
          Privacy: questions are not logged. Drafts you paste here are
          processed but not stored.
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

const EmptyState: React.FC<{ hasArtifact: boolean; isCompiling: boolean }> = ({
  hasArtifact,
  isCompiling,
}) => {
  if (isCompiling && !hasArtifact) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin mb-3 text-blue-500" />
        <p className="text-sm">Indexing comments for the first time…</p>
        <p className="text-xs text-gray-400 mt-1">
          This usually takes 1–3 minutes for a typical docket.
        </p>
      </div>
    );
  }
  if (!hasArtifact) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
        <Bot className="w-10 h-10 mb-3 text-gray-300" />
        <p className="text-sm">No comments indexed yet for this docket.</p>
      </div>
    );
  }
  return (
    <div className="text-gray-500 text-sm space-y-3">
      <p>
        Ask anything about how filers have weighed in on this docket. Examples:
      </p>
      <ul className="space-y-1.5 ml-4 list-disc text-gray-600">
        <li>"Who has commented?"</li>
        <li>"How many oppose the verification mandate?"</li>
        <li>"What does Comcast say about Title II?"</li>
        <li>"What are the hot issues?"</li>
        <li>"What court cases are cited?"</li>
        <li>"What hasn't been addressed yet?"</li>
        <li>'I\'m planning to argue [paste your draft]. Find allies.'</li>
      </ul>
    </div>
  );
};

const MessageRow: React.FC<{ message: UiMessage }> = ({ message }) => {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-blue-600 text-white rounded-lg px-3 py-2 text-sm whitespace-pre-wrap">
          {message.text}
        </div>
      </div>
    );
  }
  if (message.error) {
    return (
      <div className="flex justify-start">
        <div className="max-w-[80%] bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm">
          <div className="font-semibold mb-0.5 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> Error
          </div>
          <div>{message.error}</div>
        </div>
      </div>
    );
  }
  // Mid-stream: show partial prose with a blinking cursor cue so the
  // user sees the response materializing instead of staring at a
  // spinner. Once `onDone` fires the streamingContent gets replaced
  // with the full response (citations + metadata).
  if (message.isStreaming) {
    const partial = message.streamingContent ?? "";
    const formatted = formatMarkdown(partial);
    return (
      <div className="flex justify-start">
        <div className="max-w-[90%] bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3">
          {partial ? (
            <div
              className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed"
              dangerouslySetInnerHTML={{
                __html: formatted + '<span class="inline-block w-1.5 h-3.5 ml-0.5 bg-gray-400 animate-pulse align-baseline"></span>',
              }}
            />
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Thinking…
            </div>
          )}
        </div>
      </div>
    );
  }
  if (!message.response) return null;
  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-3">
        {message.response.answer_blocks.map((b, i) => (
          <BlockRenderer key={i} block={b} />
        ))}
        <ResponseFooter response={message.response} />
      </div>
    </div>
  );
};

const BlockRenderer: React.FC<{ block: AnswerBlock }> = ({ block }) => {
  // Markdown rendering: keep it minimal (line breaks, bold/italic, lists,
  // code spans). Avoid pulling in a full markdown library here; if richer
  // rendering is needed later, swap in `react-markdown`.
  const formatted = useMemo(() => formatMarkdown(block.content), [block.content]);

  const baseStyles =
    "text-sm text-gray-800 whitespace-pre-wrap leading-relaxed";

  // Citations attached to this specific block (vs the response-level
  // citation roll-up rendered in the footer). Synthesis / extraction
  // blocks usually have them; fast-path list blocks don't.
  const blockCites = block.citations ?? [];

  if (block.kind === "extraction") {
    return (
      <div className={`${baseStyles} bg-white border-l-4 border-blue-300 pl-3 py-1`}>
        <div dangerouslySetInnerHTML={{ __html: formatted }} />
        {blockCites.length > 0 && <CitationList citations={blockCites} compact />}
      </div>
    );
  }
  if (block.kind === "rollup") {
    return (
      <div className={`${baseStyles} bg-blue-50 px-3 py-2 rounded`}>
        <div dangerouslySetInnerHTML={{ __html: formatted }} />
      </div>
    );
  }
  if (block.kind === "synthesis") {
    return (
      <div className={`${baseStyles} bg-white border border-gray-200 rounded p-3`}>
        <div dangerouslySetInnerHTML={{ __html: formatted }} />
        {blockCites.length > 0 && (
          <div className="mt-3 pt-2 border-t border-gray-100">
            <CitationList citations={blockCites} />
          </div>
        )}
      </div>
    );
  }
  if (block.kind === "comparison") {
    return (
      <div className={`${baseStyles} bg-indigo-50 border border-indigo-100 rounded p-3`}>
        <div dangerouslySetInnerHTML={{ __html: formatted }} />
        {blockCites.length > 0 && (
          <div className="mt-3 pt-2 border-t border-indigo-100">
            <CitationList citations={blockCites} />
          </div>
        )}
      </div>
    );
  }
  if (block.kind === "gap") {
    return (
      <div className={`${baseStyles} bg-amber-50 border border-amber-200 px-3 py-2 rounded`}>
        <div dangerouslySetInnerHTML={{ __html: formatted }} />
      </div>
    );
  }
  return (
    <div className={baseStyles}>
      <div dangerouslySetInnerHTML={{ __html: formatted }} />
      {blockCites.length > 0 && (
        <div className="mt-2">
          <CitationList citations={blockCites} compact />
        </div>
      )}
    </div>
  );
};

const CitationList: React.FC<{ citations: Citation[]; compact?: boolean }> = ({
  citations,
  compact,
}) => {
  const [expanded, setExpanded] = useState(false);
  if (citations.length === 0) return null;
  // Dedupe by extraction_id (or submission_id+page+quote fallback)
  const seen = new Set<string>();
  const unique = citations.filter((c) => {
    const k = c.extraction_id || `${c.submission_id}|${c.page}|${c.quote.slice(0, 40)}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  if (compact && !expanded) {
    return (
      <button
        className="text-[11px] text-blue-600 hover:underline mt-1"
        onClick={() => setExpanded(true)}
      >
        {unique.length} citation{unique.length === 1 ? "" : "s"}
      </button>
    );
  }
  return (
    <ol className="space-y-1 text-[11px] text-gray-600">
      {unique.map((c, i) => (
        <li key={i} className="leading-snug">
          <span className="font-semibold">[{i + 1}]</span>{" "}
          <span className="text-gray-900">{c.filer_name}</span>
          {c.page > 0 && <span className="text-gray-500">, p. {c.page}</span>}
          {c.quote && (
            <span className="block pl-3 border-l-2 border-gray-300 italic text-gray-700 mt-0.5">
              "{c.quote.slice(0, 240)}{c.quote.length > 240 ? "…" : ""}"
            </span>
          )}
        </li>
      ))}
    </ol>
  );
};

const ResponseFooter: React.FC<{ response: ChatAgentResponse }> = ({ response }) => {
  const confColor = {
    high: "text-green-700 bg-green-50",
    mixed: "text-amber-700 bg-amber-50",
    low: "text-gray-600 bg-gray-100",
  }[response.confidence_summary];

  return (
    <div className="flex items-center justify-between text-[11px] text-gray-500 pt-2 border-t border-gray-200">
      <div className="flex items-center gap-2">
        <span className={`px-1.5 py-0.5 rounded ${confColor}`}>
          {response.confidence_summary} confidence
        </span>
        {response.fast_path && (
          <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">
            instant
          </span>
        )}
        {response.intent && (
          <span className="text-gray-400">via {response.intent}</span>
        )}
      </div>
      {response.citations.length > 0 && (
        <span>{response.citations.length} citations</span>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tiny markdown formatter — handles the subset we emit.
// ---------------------------------------------------------------------------
function formatMarkdown(text: string): string {
  const escaped = escapeHtml(text);
  return escaped
    // Headers — process before inline rules so the # tokens don't get
    // mangled. ### → small, ## → medium, # → large.
    .replace(/^### (.+)$/gm, '<h4 class="font-semibold text-gray-900 mt-3 mb-1">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 class="font-semibold text-gray-900 mt-3 mb-1">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 class="font-semibold text-gray-900 mt-3 mb-1">$1</h2>')
    // Bold
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    // Italic (single-asterisk OR underscore-pair, since the
    // synthesizer prefers _italic_ for inline coverage notes)
    .replace(/(^|[^*])\*([^*]+)\*([^*]|$)/g, "$1<em>$2</em>$3")
    .replace(/(^|[^_])_([^_]+)_([^_]|$)/g, "$1<em>$2</em>$3")
    // Inline code
    .replace(/`([^`]+)`/g, "<code class=\"bg-gray-100 px-1 rounded text-[13px]\">$1</code>")
    // Markdown links — [text](url)
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:underline">$1</a>',
    )
    // Block quotes
    .replace(/^> (.+)$/gm, '<div class="border-l-2 border-gray-300 pl-2 italic text-gray-600">$1</div>')
    // Bullet lists (must come after other line-based rules)
    .replace(/^- (.+)$/gm, "<li>$1</li>")
    .replace(/(<li>[\s\S]+<\/li>)/g, '<ul class="list-disc ml-4 space-y-1">$1</ul>');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export default CommentArtifactChat;
